import { useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { UseCaseFormData } from "../../types/use-case.types"

import { Spinner } from "@/components/ui/spinner"
import { SettingsService } from "@/services/settings-service"
import { useUseCasesStore } from "@/stores/use-cases-store"

import { UseCasesGreeting } from "./use-cases-greeting"
import { UseCasesHeroCards } from "./use-cases-hero-cards"
import { useCreateUseCase } from "../../hooks/use-create-use-case"
import { useUseCases } from "../../hooks/use-use-cases"
import { NewUseCaseWizard } from "../wizard/new-use-case-wizard"

type WizardInitPath = "ai" | "template" | null

export function UseCasesLanding() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setActiveTab, setSelectedUseCaseId } = useUseCasesStore()
  const { data: useCases, isLoading } = useUseCases()

  const userName = SettingsService.getInstance().claims.name
  const hasUseCases = (useCases?.length ?? 0) > 0

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardInitPath, setWizardInitPath] = useState<WizardInitPath>(null)

  const handleAiQuickStart = useCallback(() => {
    setWizardInitPath("ai")
    setWizardOpen(true)
  }, [])

  const handleTemplate = useCallback(() => {
    setWizardInitPath("template")
    setWizardOpen(true)
  }, [])

  const handleScratch = useCallback(() => {
    setSelectedUseCaseId(null)
    setActiveTab("factsheet")
    void navigate("/ai-portfolio/dashboard")
  }, [setSelectedUseCaseId, setActiveTab, navigate])

  const createMutation = useCreateUseCase()

  const handleWizardComplete = useCallback(
    async (data: UseCaseFormData) => {
      // Inject owner name
      const formData: UseCaseFormData = { ...data, owner: data.owner || userName || "" }

      // Create the use case in the backend
      const result = await createMutation.mutateAsync(formData)

      // Navigate to dashboard with the new use case selected
      setSelectedUseCaseId(result.id)
      setActiveTab("factsheet")
      void navigate("/ai-portfolio/dashboard")
    },
    [userName, createMutation, setSelectedUseCaseId, setActiveTab, navigate]
  )

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="size-8" />
      </div>
    )
  }

  return (
    <div className="relative flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] w-full flex-col overflow-hidden">
      {/* Background gradients */}
      <div className="from-secondary/25 via-secondary/10 to-secondary/5 pointer-events-none absolute inset-0 bg-gradient-to-tl" />
      <div className="from-secondary/20 via-secondary/10 pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_80%_80%,_var(--tw-gradient-stops))] to-transparent" />

      {/* Content */}
      <main className="relative flex-1 overflow-y-auto">
        {/* Hero section */}
        <div className="relative flex min-h-[calc(100vh-26rem)] flex-col">
          <div className="relative flex flex-1 flex-col items-center justify-center px-6 pt-40 pb-8">
            <UseCasesGreeting userName={userName} />
            <p className="text-muted-foreground mb-8 text-center">
              {t("useCases.landing.subtitle")}
            </p>
            <UseCasesHeroCards
              hasUseCases={hasUseCases}
              onAiQuickStart={handleAiQuickStart}
              onScratch={handleScratch}
              onTemplate={handleTemplate}
            />
          </div>
        </div>
      </main>

      {/* Bottom fade */}
      <div className="from-background/80 pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent" />

      {/* Wizard Dialog */}
      <NewUseCaseWizard
        initPath={wizardInitPath}
        open={wizardOpen}
        onComplete={handleWizardComplete}
        onOpenChange={setWizardOpen}
      />
    </div>
  )
}
